import { FC, useMemo, useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Box,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useStudentDetailsFeesHook, useUpdateStudentFeesMutation } from '../../../../../../apis/students/studentDetails/useStudentDetailsService';
import { TStatus } from 'src/apis/finance/finance.interfaces';
import { PermissionGate } from 'src/components/rbac/PermissionGate';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ControlledTextField from '../../../../../components/controlledComponents/ControlledTextField.component';
import { zNumberFromInput } from 'src/utils/zodFormHelpers';

interface StudentFeesTabProps {
  studentId: string;
}

const feeOptionSchema = z.object({
  feeId: z.string(),
  paidAmount: zNumberFromInput({ min: 0 }),
  pendingAmount: zNumberFromInput({ min: 0 }),
});

const FeesFormSchema = z.object({
  feeOptions: z.array(feeOptionSchema),
});

type FeesFormData = z.infer<typeof FeesFormSchema>;

const StudentFeesTab: FC<StudentFeesTabProps> = ({ studentId }) => {
  const { translateText } = useTranslation();
  const { data, isLoading, error } = useStudentDetailsFeesHook(studentId);
  const updateFeesMutation = useUpdateStudentFeesMutation();
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FeesFormData>({
    resolver: zodResolver(FeesFormSchema),
    defaultValues: { feeOptions: [] },
  });

  const feeOptionsWithStatus = useMemo(() => {
    if (!data) return [];
    const isOptionalMap = (data.feeStructure || []).reduce(
      (acc, fs) => {
        acc[fs.feeId] = fs.isOptional ?? false;
        return acc;
      },
      {} as Record<string, boolean>
    );
    return data.feeComponents.map((component) => ({
      ...component,
      isOptional: component.isOptional ?? isOptionalMap[component.feeId] ?? false,
    }));
  }, [data]);

  const initialOptSelections = useMemo(
    () =>
      feeOptionsWithStatus.reduce(
        (acc, c) => {
          acc[c.feeId] = !c.isOptedOut;
          return acc;
        },
        {} as Record<string, boolean>
      ),
    [feeOptionsWithStatus]
  );

  const [optSelections, setOptSelections] = useState<Record<string, boolean>>(initialOptSelections);

  useEffect(() => {
    setOptSelections(initialOptSelections);
  }, [initialOptSelections]);

  const selectedComponents = useMemo(
    () => feeOptionsWithStatus.filter((c) => optSelections[c.feeId] !== false),
    [feeOptionsWithStatus, optSelections]
  );

  const handleOptToggle = (feeId: string, checked: boolean) => {
    setOptSelections((prev) => ({ ...prev, [feeId]: checked }));
  };

  useEffect(() => {
    if (data) {
      const formData: FeesFormData = {
        feeOptions: feeOptionsWithStatus.map((fee) => ({
          feeId: fee.feeId,
          paidAmount: fee.paidAmount,
          pendingAmount: fee.pendingAmount,
        })),
      };
      reset(formData);
    }
  }, [data, feeOptionsWithStatus, reset]);

  const onSubmit = (formData: FeesFormData) => {
    const selectedIds = new Set(selectedComponents.map((c) => c.feeId));
    updateFeesMutation.mutate(
      {
        studentId,
        fees: {
          feeComponents: formData.feeOptions
            .filter((option) => selectedIds.has(option.feeId))
            .map((option) => ({
              feeId: option.feeId,
              paidAmount: option.paidAmount,
              pendingAmount: option.pendingAmount,
            })),
        },
      },
      {
        onSuccess: () => {
          setIsEditMode(false);
        },
      }
    );
  };

  const getStatusColor = (status: TStatus) => {
    switch (status) {
      case TStatus.PAID:
        return 'success';
      case TStatus.PARTIAL:
        return 'warning';
      case TStatus.UNPAID:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: TStatus) => {
    switch (status) {
      case TStatus.PAID:
        return translateText('students.fees.paid');
      case TStatus.PARTIAL:
        return translateText('students.fees.partial');
      case TStatus.UNPAID:
        return translateText('students.fees.unpaid');
      default:
        return 'N/A';
    }
  };

  if (!studentId) {
    return (
      <Alert severity="info" data-testid="student-fees-tab-no-student">
        {translateText('students.studentDetails.fees.noStudentId')}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="40vh"
        data-testid="student-fees-tab-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" data-testid="student-fees-tab-error">
        {translateText('common.error')}
      </Alert>
    );
  }

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      sx={{ p: { xs: 1, sm: 2 } }}
      data-testid="student-fees-tab-container"
    >
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          data-testid="student-fees-tab-header"
        >
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {translateText('students.studentDetails.fees.title')}
          </Typography>
          <PermissionGate
            module="students"
            submodule="fees"
            permission="write"
          >
            {!isEditMode ? (
              <Button
                variant="contained"
                onClick={() => setIsEditMode(true)}
                sx={{
                  minHeight: { xs: '44px', sm: '36px' },
                  fontSize: { xs: '16px', sm: '14px' },
                }}
                data-testid="student-fees-tab-edit-button"
              >
                {translateText('common.edit')}
              </Button>
            ) : (
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsEditMode(false);
                    reset();
                    setOptSelections(initialOptSelections);
                  }}
                  sx={{
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                  }}
                  data-testid="student-fees-tab-cancel-button"
                >
                  {translateText('common.cancel')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  disabled={updateFeesMutation.isPending}
                  sx={{
                    minHeight: { xs: '44px', sm: '36px' },
                    fontSize: { xs: '16px', sm: '14px' },
                  }}
                  data-testid="student-fees-tab-save-button"
                >
                  {translateText('common.save')}
                </Button>
              </Box>
            )}
          </PermissionGate>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Grid
              container
              spacing={{ xs: 1, sm: 2 }}
              sx={{
                py: { xs: 1, sm: 1.5 },
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'action.hover',
              }}
              data-testid="fee-table-header"
            >
              <Grid item xs={12} md={3}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('students.fees.feeType')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('common.status')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('students.fees.expectedAmount')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('students.fees.paidAmount')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('students.fees.pendingAmount')}
                </Typography>
              </Grid>
            </Grid>

            {feeOptionsWithStatus.map((feeOption, index) => {
              const isOptedOutDisplay = !optSelections[feeOption.feeId];
              const showOptToggle = isEditMode && feeOption.isOptional;
              return isOptedOutDisplay ? (
                <Grid
                  container
                  spacing={{ xs: 1, sm: 2 }}
                  alignItems="center"
                  key={feeOption.feeId}
                  sx={{
                    py: { xs: 1, sm: 2 },
                    borderBottom:
                      index < feeOptionsWithStatus.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    opacity: 0.7,
                  }}
                  data-testid={`fee-row-${index}`}
                >
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        textDecoration: 'line-through',
                      }}
                    >
                      {feeOption.feeType}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={4} mt={0.5}>
                      <Chip
                        label={translateText('students.fees.optedOut')}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                      />
                      {showOptToggle && (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={false}
                              onChange={(_, checked) => handleOptToggle(feeOption.feeId, checked)}
                              size="small"
                              data-testid={`fee-opt-toggle-${feeOption.feeId}`}
                            />
                          }
                          label={translateText('students.fees.include')}
                          sx={{ display: 'flex', gap: 1, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Chip
                      label={getStatusLabel(feeOption.status)}
                      color={getStatusColor(feeOption.status) as any}
                      size="small"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography variant="body1" sx={{ textDecoration: 'line-through' }}>
                      ₹{feeOption.feeAmount.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography variant="body1" sx={{ textDecoration: 'line-through' }}>
                      ₹{feeOption.paidAmount.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid
                  container
                  spacing={{ xs: 1, sm: 2 }}
                  alignItems="center"
                  key={feeOption.feeId}
                  sx={{
                    py: { xs: 1, sm: 2 },
                    borderBottom:
                      index < feeOptionsWithStatus.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                  }}
                  data-testid={`fee-row-${index}`}
                >
                  <Grid item xs={12} md={3}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      {feeOption.feeType}
                    </Typography>
                    {showOptToggle && (
                      <Box display="flex" alignItems="center" gap={4} mt={0.5}>
                        <Chip
                          label={translateText('students.fees.optedIn')}
                          size="small"
                          variant="outlined"
                          color="success"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={true}
                              onChange={(_, checked) => handleOptToggle(feeOption.feeId, checked)}
                              size="small"
                              data-testid={`fee-opt-toggle-${feeOption.feeId}`}
                            />
                          }
                          label={translateText('students.fees.include')}
                          sx={{ display: 'flex', gap: 1, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                        />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Chip
                      label={getStatusLabel(feeOption.status)}
                      color={getStatusColor(feeOption.status) as any}
                      size="small"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      ₹{feeOption.feeAmount.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      ₹{feeOption.paidAmount.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    {isEditMode && feeOption.pendingAmount > 0 ? (
                      <ControlledTextField
                        id={`pending-amount-${feeOption.feeId}`}
                        label=""
                        name={`feeOptions.${index}.pendingAmount`}
                        control={control}
                        type="number"
                        errorMessage={
                          errors.feeOptions?.[index]?.pendingAmount?.message
                        }
                        isGridItem={false}
                      />
                    ) : feeOption.pendingAmount > 0 ? (
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        }}
                      >
                        ₹{feeOption.pendingAmount.toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color="success.main"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {translateText('students.fees.fullyPaid')}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              );
            })}

            <Grid
              container
              spacing={{ xs: 1, sm: 2 }}
              sx={{
                py: { xs: 1, sm: 2 },
                mt: { xs: 0.5, sm: 1 },
                borderTop: 2,
                borderColor: 'primary.main',
                backgroundColor: 'action.selected',
              }}
              data-testid="fee-table-footer"
            >
              <Grid item xs={12} md={3}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {translateText('students.fees.total')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2} />
              <Grid item xs={12} md={2}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  ₹
                  {selectedComponents
                    .reduce((acc, fee) => acc + fee.feeAmount, 0)
                    .toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  ₹
                  {selectedComponents
                    .reduce((acc, fee) => acc + fee.paidAmount, 0)
                    .toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  ₹
                  {selectedComponents
                    .reduce((acc, fee) => acc + fee.pendingAmount, 0)
                    .toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default StudentFeesTab;
